import {
	FunctionComponent,
	MutableRefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { UseFormSetValue } from "react-hook-form";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { faArrowUpRightFromSquare } from "@fortawesome/pro-regular-svg-icons";

import { ROUTE_CONFIG } from "@/routes/config";

import { Toggle } from "@/components/Toggle/Toggle";
import { SelectField } from "@/components/SelectField/SelectField";
import { Icon } from "@/components/Icon/Icon";
import {
	MultiAutoComplete,
	MultiAutoCompleteItem,
} from "@/components/MultiAutoComplete/MultiAutoComplete";

import { useLazyGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";

import {
	CompanyEnrichAutoComplete,
	CompanyEnrichAutoCompleteItem,
} from "./CompanyEnrichAutoComplete";
import { CreateCompanyEnrich, ExcludeCompanies } from "../schema/companyEnrich";
import { DEPARTMENT_OPTIONS } from "../constants/department.constants";
import { normalizeDomainInput } from "../utils/normalizeDomainInput";

interface PersonaCategory {
	id?: number;
	category: string;
	jobsToInclude: string[];
}

interface ExcludeCompaniesSectionProps {
	formValues: Partial<CreateCompanyEnrich>;
	setValue: UseFormSetValue<CreateCompanyEnrich>;
	fetchCompanyDomains: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchPositions: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	fetchCountries: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	countryNameMap: MutableRefObject<Map<string, string>>;
	personaCategories?: PersonaCategory[];
}

export const ExcludeCompaniesSection: FunctionComponent<ExcludeCompaniesSectionProps> = ({
	formValues,
	setValue,
	fetchCompanyDomains,
	fetchPositions,
	fetchCountries,
	countryNameMap,
	personaCategories = [],
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companyEnrich.${key}`);

	const exclude = formValues.excludeCompanies ?? {};
	const [selectedListItems, setSelectedListItems] = useState<CompanyEnrichAutoCompleteItem[]>(
		(exclude.listIds ?? []).map((id) => ({ id: String(id), title: String(id) }))
	);
	const [selectedExcludePersonaCategories, setSelectedExcludePersonaCategories] = useState<
		MultiAutoCompleteItem[]
	>([]);

	const manualExcludePositionQueryRef = useRef<string[]>([]);
	const personaExcludePositionJobsRef = useRef<string[]>([]);

	const [triggerGetLeanLists] = useLazyGetLeanListsQuery();

	useEffect(() => {
		const personaJobs = new Set(personaExcludePositionJobsRef.current);

		manualExcludePositionQueryRef.current = (formValues.excludePositionQuery ?? []).filter(
			(p) => !personaJobs.has(p)
		);
	}, [formValues.excludePositionQuery]);

	const update = (next: NonNullable<ExcludeCompanies>) => {
		setValue("excludeCompanies", next, { shouldDirty: true, shouldValidate: true });
	};

	const fetchLists = useCallback(
		async (query: string): Promise<CompanyEnrichAutoCompleteItem[]> => {
			const result = await triggerGetLeanLists({ page: 1, limit: 20, searchValue: query }).unwrap();

			return (result.data ?? []).map((list) => ({ id: String(list.id), title: list.name }));
		},
		[triggerGetLeanLists]
	);

	const handleExcludePersonaCategorySelect = (items?: MultiAutoCompleteItem[]) => {
		setSelectedExcludePersonaCategories(items || []);

		const personaJobs = [
			...new Set(
				(items || []).flatMap(
					(item) => personaCategories.find((c) => c.id === item.id)?.jobsToInclude ?? []
				)
			),
		];

		personaExcludePositionJobsRef.current = personaJobs;

		const merged = [...new Set([...manualExcludePositionQueryRef.current, ...personaJobs])];

		setValue("excludePositionQuery", merged, { shouldDirty: true, shouldValidate: true });
	};

	return (
		<div className="flex flex-col gap-6 w-full">
			{/* Exclude leads */}
			<div className="flex flex-col gap-4">
				<div className="text-sm text-text font-semibold">{ts("excludeLeadsBoxTitle")}</div>

				{personaCategories.length > 0 && (
					<MultiAutoComplete
						stayOn
						data={personaCategories
							.filter((c) => c.id != null)
							.map((c) => ({ id: c.id as number, title: c.category }))}
						handleSelect={handleExcludePersonaCategorySelect}
						label={ts("excludePersonaCategoryLabel")}
						placeholder={ts("excludePersonaCategoryPlaceholder")}
						selectedItems={selectedExcludePersonaCategories}
					/>
				)}

				<CompanyEnrichAutoComplete
					allowCustomValues
					fetchFn={fetchPositions}
					handleSelect={(value) => {
						const positions = value?.map((r) => r.id) || [];

						manualExcludePositionQueryRef.current = positions;
						personaExcludePositionJobsRef.current = personaExcludePositionJobsRef.current.filter(
							(job) => positions.includes(job)
						);

						const merged = [...new Set([...positions, ...personaExcludePositionJobsRef.current])];

						setValue("excludePositionQuery", merged, { shouldDirty: true, shouldValidate: true });
					}}
					label={ts("excludePositionQueryLabel")}
					placeholder={ts("excludePositionQueryPlaceholder")}
					selectedItems={formValues.excludePositionQuery?.map((r) => ({ id: r, title: r })) || []}
					testId="autocomplete-excludePositionQuery"
				/>

				<MultiAutoComplete
					stayOn
					data={DEPARTMENT_OPTIONS}
					handleSelect={(value) =>
						setValue("excludeDepartment", value?.map((r) => r.id.toString()) || [], {
							shouldDirty: true,
							shouldValidate: true,
						})
					}
					label={ts("excludeDepartmentLabel")}
					placeholder={ts("excludeDepartmentPlaceholder")}
					selectedItems={
						formValues.excludeDepartment?.map((r) => ({
							id: r,
							title: DEPARTMENT_OPTIONS.find((d) => d.id === r)?.title || r,
						})) || []
					}
				/>

				<CompanyEnrichAutoComplete
					stayOn
					fetchFn={fetchCountries}
					handleSelect={(value) =>
						setValue("excludeCountries", value?.map((r) => r.id) || [], {
							shouldDirty: true,
							shouldValidate: true,
						})
					}
					label={ts("excludeCountriesLabel")}
					placeholder={ts("excludeCountriesPlaceholder")}
					selectedItems={
						formValues.excludeCountries?.map((r) => ({
							id: r,
							title: countryNameMap.current.get(r) || r,
						})) || []
					}
					testId="autocomplete-excludeCountries"
				/>
			</div>

			{/* Exclude companies */}
			<div className="flex flex-col gap-4">
				<div className="text-sm text-text font-semibold">{ts("excludeCompaniesBoxTitle")}</div>

				<SelectField
					handleSelect={(value) =>
						update({ ...exclude, scope: (value as "known" | "lists" | null) ?? null })
					}
					label={ts("excludeCompaniesScopeLabel")}
					name="excludeCompaniesScope"
					options={[
						{ value: "known", title: ts("excludeCompaniesScopeKnown") },
						{ value: "lists", title: ts("excludeCompaniesScopeLists") },
					]}
					placeholder={ts("excludeCompaniesScopePlaceholder")}
					selectedOption={exclude.scope ?? null}
					testId="select-exclude-companies-scope"
				/>

				{exclude.scope === "lists" && (
					<CompanyEnrichAutoComplete
						fetchFn={fetchLists}
						handleSelect={(value) => {
							setSelectedListItems(value ?? []);
							update({ ...exclude, listIds: (value ?? []).map((item) => Number(item.id)) });
						}}
						label={ts("excludeCompaniesListsLabel")}
						placeholder={ts("excludeCompaniesListsPlaceholder")}
						selectedItems={selectedListItems}
						testId="autocomplete-exclude-companies-lists"
					/>
				)}

				<div className="flex items-center gap-3">
					<Toggle
						isChecked={!!exclude.excludeCompetitors}
						onChange={() => update({ ...exclude, excludeCompetitors: !exclude.excludeCompetitors })}
					/>
					<div className="flex-1">
						<div className="text-sm text-text font-medium">{ts("excludeCompaniesCompetitors")}</div>
						<div className="text-ssm text-text-meta">
							{ts("excludeCompaniesCompetitorsDescription")}
						</div>
					</div>
					<Link
						className="flex items-center gap-1.5 text-xs font-semibold text-blue-action hover:underline whitespace-nowrap"
						target="_blank"
						to={ROUTE_CONFIG.DO_NOT_CONTACT_COMPETITORS}
					>
						{ts("excludeCompaniesReviewCompetitors")}
						<Icon className="!w-[11px] h-auto" icon={faArrowUpRightFromSquare} />
					</Link>
				</div>

				<div>
					<div className="text-sm text-text font-medium mb-2">{ts("excludeCompaniesDomains")}</div>
					<CompanyEnrichAutoComplete
						allowCustomValues
						normalizeCustomValue={normalizeDomainInput}
						fetchFn={fetchCompanyDomains}
						handleSelect={(value) =>
							setValue("excludeDomains", value?.map((r) => r.id) || [], {
								shouldDirty: true,
								shouldValidate: true,
							})
						}
						placeholder={ts("excludeCompaniesDomainsPlaceholder")}
						selectedItems={(formValues.excludeDomains ?? []).map((domain) => ({
							id: domain,
							title: domain,
						}))}
						testId="autocomplete-exclude-companies-domains"
					/>
				</div>
			</div>
		</div>
	);
};
